/* eslint-disable */

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";

export default function AgreementVerificationPage() {
  const [params] = useSearchParams();
  const agreementId = params.get("agreementId");

  const [loading, setLoading] = useState(true);
  const [agreement, setAgreement] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadAgreement() {
      try {
        setLoading(true);
        setError("");

        if (!agreementId) {
          setError("No agreement ID was provided.");
          return;
        }

        const callable = httpsCallable(functions, "verifyAgreementPublic");
        const res = await callable({ agreementId });

        setAgreement(res.data?.agreement || null);
      } catch (err) {
        console.error(err);
        setError(err?.message || "Could not verify agreement.");
      } finally {
        setLoading(false);
      }
    }

    loadAgreement();
  }, [agreementId]);

  const safe = useMemo(() => {
    const signedBy =
      agreement?.signerName ||
      agreement?.signedByName ||
      agreement?.signedBy ||
      agreement?.recipientName ||
      "-";

    const signedEmail =
      agreement?.signerEmail ||
      agreement?.signedByEmail ||
      agreement?.recipientEmail ||
      "-";

    const signerRole =
      agreement?.signerRole ||
      agreement?.signedByRole ||
      agreement?.role ||
      "School Administrator";

    const hash =
      agreement?.pdfHash ||
      agreement?.agreementHash ||
      agreement?.hash ||
      "-";

    return {
      schoolName: agreement?.schoolName || "School Agreement",
      agreementVersion: agreement?.agreementVersion || "-",
      signedAt: formatDate(agreement?.signedAt),
      signedBy,
      signedEmail,
      signerRole,
      hash,
      pdfUrl: agreement?.pdfUrl || "",
      verificationStatus: agreement?.verificationStatus || "valid",
    };
  }, [agreement]);

  if (loading) return <Centered text="Verifying signed agreement..." />;

  if (error) {
    return (
      <Page>
        <Card>
          <Badge tone="bad">Verification failed</Badge>
          <h1 style={styles.title}>Agreement not verified</h1>
          <p style={styles.text}>{error}</p>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <Card>
        <div style={styles.topRow}>
          <Badge tone="good">Valid signed agreement</Badge>
          <div style={styles.seal}>Verified by PresentPlus</div>
        </div>

        <div style={styles.heroGrid}>
          <div>
            <h1 style={styles.title}>{safe.schoolName}</h1>
            <p style={styles.text}>
              This agreement record has been validated against the PresentPlus
              immutable agreement register. The SHA-256 hash confirms the stored
              signed agreement snapshot.
            </p>
          </div>

          <div style={styles.shieldCard}>
            <div style={styles.shieldIcon}>✓</div>
            <strong>Integrity verified</strong>
            <span>No alteration detected in the stored agreement record.</span>
          </div>
        </div>

        <div style={styles.grid}>
          <Meta label="Agreement ID" value={agreementId} />
          <Meta label="Agreement Version" value={safe.agreementVersion} />
          <Meta label="Signed By" value={safe.signedBy} />
          <Meta label="Signer Role" value={safe.signerRole} />
          <Meta label="Signed Email" value={safe.signedEmail} />
          <Meta label="Signed At" value={safe.signedAt} />
        </div>

        <div style={styles.hashBox}>
          <div>
            <div style={styles.hashLabel}>SHA-256 verification hash</div>
            <div style={styles.hashText}>{safe.hash}</div>
          </div>
          <div style={styles.hashStatus}>VALID</div>
        </div>

        <div style={styles.timeline}>
          <TimelineItem title="Agreement issued" text="A secure school agreement invitation was created." />
          <TimelineItem title="Agreement signed" text="The school representative completed the signing flow." />
          <TimelineItem title="Immutable snapshot stored" text="A locked agreement record and hash were generated." />
          <TimelineItem title="Verification available" text="This public portal can verify the agreement record." />
        </div>

        <div style={styles.actions}>
          {safe.pdfUrl ? (
            <button
              type="button"
              style={styles.primaryBtn}
              onClick={() => window.open(safe.pdfUrl, "_blank", "noopener,noreferrer")}
            >
              Download signed PDF
            </button>
          ) : null}

          <button
            type="button"
            style={styles.secondaryBtn}
            onClick={() => {
              window.location.href = "/";
            }}
          >
            Return to PresentPlus
          </button>
        </div>

        <div style={styles.footerNote}>
          PresentPlus agreement verification confirms the stored record and
          cryptographic hash held by CoreLogic Systems Ltd.
        </div>
      </Card>
    </Page>
  );
}

function Page({ children }) {
  return <div style={styles.page}>{children}</div>;
}

function Card({ children }) {
  return <div style={styles.card}>{children}</div>;
}

function Badge({ children, tone }) {
  return <div style={tone === "bad" ? styles.badBadge : styles.goodBadge}>{children}</div>;
}

function Meta({ label, value }) {
  return (
    <div style={styles.metaCard}>
      <div style={styles.metaLabel}>{label}</div>
      <div style={styles.metaValue}>{value || "-"}</div>
    </div>
  );
}

function TimelineItem({ title, text }) {
  return (
    <div style={styles.timelineItem}>
      <div style={styles.timelineDot}>✓</div>
      <div>
        <strong>{title}</strong>
        <p>{text}</p>
      </div>
    </div>
  );
}

function Centered({ text }) {
  return <div style={styles.centered}>{text}</div>;
}

function formatDate(value) {
  try {
    if (!value) return "-";

    if (value?.seconds) {
      return new Date(value.seconds * 1000).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    if (typeof value === "string") return value;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
}

const styles = {
  centered: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    background: "#f4f7fb",
    color: "#334155",
    fontWeight: 900,
    fontSize: 18,
  },

  page: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: 24,
    background:
      "radial-gradient(circle at top right, rgba(124,58,237,0.20), transparent 32%), radial-gradient(circle at left, rgba(37,99,235,0.12), transparent 28%), linear-gradient(135deg,#eef5ff 0%,#f8fbff 55%,#f3e8ff 100%)",
  },

  card: {
    width: "100%",
    maxWidth: 1050,
    borderRadius: 38,
    background: "rgba(255,255,255,0.92)",
    border: "1px solid #dbe7ff",
    boxShadow: "0 34px 100px rgba(15,23,42,0.14)",
    padding: 44,
    backdropFilter: "blur(18px)",
  },

  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 22,
  },

  goodBadge: {
    display: "inline-flex",
    borderRadius: 999,
    padding: "10px 14px",
    background: "#ecfdf3",
    color: "#166534",
    fontWeight: 950,
  },

  badBadge: {
    display: "inline-flex",
    borderRadius: 999,
    padding: "10px 14px",
    background: "#fff1f2",
    color: "#be123c",
    fontWeight: 950,
    marginBottom: 18,
  },

  seal: {
    borderRadius: 999,
    padding: "10px 14px",
    background: "#eef2ff",
    color: "#4338ca",
    fontWeight: 950,
    border: "1px solid #dbe7ff",
  },

  heroGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 280px",
    gap: 22,
    alignItems: "stretch",
  },

  title: {
    margin: 0,
    fontSize: 52,
    lineHeight: 0.95,
    letterSpacing: "-0.07em",
    color: "#0f172a",
    fontWeight: 950,
  },

  text: {
    marginTop: 18,
    color: "#475569",
    lineHeight: 1.75,
    fontSize: 16,
    maxWidth: 760,
  },

  shieldCard: {
    borderRadius: 28,
    padding: 22,
    background: "linear-gradient(135deg,#0f172a 0%,#1e1b4b 100%)",
    color: "#ffffff",
    display: "grid",
    alignContent: "center",
    gap: 10,
    boxShadow: "0 24px 54px rgba(15,23,42,0.20)",
  },

  shieldIcon: {
    width: 52,
    height: 52,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    background: "#dcfce7",
    color: "#166534",
    fontSize: 26,
    fontWeight: 950,
  },

  grid: {
    marginTop: 28,
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 14,
  },

  metaCard: {
    borderRadius: 22,
    background: "#f8fbff",
    border: "1px solid #dbeafe",
    padding: 18,
    minHeight: 88,
  },

  metaLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.13em",
    color: "#64748b",
    fontWeight: 950,
    marginBottom: 9,
  },

  metaValue: {
    color: "#0f172a",
    fontWeight: 950,
    wordBreak: "break-word",
  },

  hashBox: {
    marginTop: 18,
    borderRadius: 26,
    background: "#0f172a",
    color: "#ffffff",
    padding: 22,
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    alignItems: "center",
  },

  hashLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    color: "#cbd5e1",
    fontWeight: 950,
    marginBottom: 10,
  },

  hashText: {
    fontFamily: "monospace",
    fontSize: 13,
    color: "#ffffff",
    wordBreak: "break-all",
  },

  hashStatus: {
    borderRadius: 999,
    padding: "10px 14px",
    background: "#dcfce7",
    color: "#166534",
    fontWeight: 950,
  },

  timeline: {
    marginTop: 22,
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
  },

  timelineItem: {
    borderRadius: 22,
    background: "#ffffff",
    border: "1px solid #e5edff",
    padding: 16,
    display: "grid",
    gap: 10,
  },

  timelineDot: {
    width: 28,
    height: 28,
    borderRadius: 999,
    background: "#ecfdf3",
    color: "#166534",
    display: "grid",
    placeItems: "center",
    fontWeight: 950,
  },

  actions: {
    marginTop: 26,
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    flexWrap: "wrap",
  },

  primaryBtn: {
    border: "none",
    borderRadius: 18,
    padding: "15px 22px",
    background: "linear-gradient(135deg,#2563eb 0%,#7c3aed 100%)",
    color: "#ffffff",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 18px 40px rgba(79,70,229,0.24)",
  },

  secondaryBtn: {
    border: "1px solid #dbe7ff",
    borderRadius: 18,
    padding: "15px 22px",
    background: "#ffffff",
    color: "#334155",
    fontWeight: 950,
    cursor: "pointer",
  },

  footerNote: {
    marginTop: 22,
    color: "#64748b",
    textAlign: "center",
    fontSize: 13,
    fontWeight: 800,
  },
};